import { useApiClient } from "@lib/utils/hooks/useApiClient";
import {
  FacilityDetailsResponse,
  FacilitySummaryResponse,
  MapFilters,
  NestedMultipleValueFilter,
  PathResponse,
  SearchParams,
  SingleValueFilterOption,
} from "@repo/shared-types";
import { useEffect, useState } from "react";

import { useMapContext } from "./MapProvider";
import { useMapSectionCachedPaths } from "./useMapSectionCachedPaths";
import { useMapSectionQuery } from "./useMapSectionQuery";

interface PathValue {
  id: string;
  name: string;
  value: string;
  label: string;
  type: string;
  pathType: string | null;
}

export function useMapSectionData() {
  const query = useMapSectionQuery();
  const apiClient = useApiClient();
  const { recentlyChosenFilter } = useMapContext();
  const [filters, setFilters] = useState<MapFilters>({ ids: [], entries: [] });
  const { paths, setPaths, searchPaths } = useMapSectionCachedPaths();
  const [facilities, setFacilities] = useState<FacilitySummaryResponse[]>([]);
  const [poi, setPoi] = useState<FacilityDetailsResponse | null>(null);
  const serializedQuery = JSON.stringify(query);

  useEffect(() => {
    async function loadMapData(searchParams: SearchParams) {
      const promises = [
        apiClient.getEditableMapFilters(
          searchParams.type ? { type: searchParams.type } : {}
        ),
        searchPaths({
          ...searchParams,
          pathType:
            recentlyChosenFilter?.value === "loop"
              ? "loops"
              : recentlyChosenFilter?.value === "linear"
                ? "linear"
                : undefined,
        }),
        apiClient.searchFacilitiesV2(searchParams),
        searchParams.poi
          ? apiClient.searchFacility(searchParams.poi as string)
          : null,
      ] as const;

      const mapData = await Promise.all(promises);

      const filteredFilters = mapData[0];
      const filteredPaths = mapData[1] as PathResponse[];
      const filteredFacilities = mapData[2];
      const filteredPoi = mapData[3];

      if (
        recentlyChosenFilter?.name === "pathType" &&
        recentlyChosenFilter?.value
      ) {
        const updatedFilters = {
          ...filteredFilters,
          entries: filteredFilters.entries.map(entry => {
            if (entry.id === "Regions" && entry.type === "multiple") {
              const nestedEntry = entry as NestedMultipleValueFilter<
                string,
                any
              >;
              const filteredValues = nestedEntry.values
                .map((region: SingleValueFilterOption<string>) => {
                  if (!Array.isArray(region.value)) return null;

                  const filteredPaths = region.value.filter(
                    (path: PathValue) => {
                      const filterValue = recentlyChosenFilter.value;
                      const pathType = path.pathType;
                      return (
                        (filterValue === "loop" && pathType === "loops") ||
                        (filterValue === "linear" && pathType === null)
                      );
                    }
                  );

                  if (filteredPaths.length === 0) return null;

                  return {
                    ...region,
                    value: filteredPaths,
                  };
                })
                .filter(
                  (
                    region: SingleValueFilterOption<string> | null
                  ): region is SingleValueFilterOption<string> =>
                    region !== null
                );

              return {
                ...nestedEntry,
                values: filteredValues,
              };
            }
            return entry;
          }),
        };

        setFilters(updatedFilters);
        setPaths(filteredPaths);
        setFacilities(filteredFacilities);
        setPoi(filteredPoi);
      } else {
        setFilters(filteredFilters);
        setPaths(filteredPaths);
        setFacilities(filteredFacilities);
        setPoi(filteredPoi);
      }
    }

    loadMapData(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedQuery, recentlyChosenFilter]);

  return { filters, paths, facilities, poi };
}
